import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Instagram, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { JsonLd } from "../components/site/JsonLd";
import { Lightbox } from "../components/site/Lightbox";

export default function ArtistProfile() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["artist", slug],
    queryFn: async () => (await api.get(`/artists/${slug}`)).data,
    retry: false,
  });

  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));

  if (isLoading) return <div className="pt-44 text-center text-[#828290] font-mono text-sm">Loading…</div>;
  if (isError || !data) return (
    <div className="pt-44 pb-32 text-center px-6">
      <h1 className="display text-5xl text-white mb-4">Artist not found.</h1>
      <Link to="/artists" className="text-ember font-mono text-xs uppercase tracking-[0.2em] link-underline">Back to artists</Link>
    </div>
  );

  const { artist, portfolio } = data;

  return (
    <>
      <Seo title={`${artist.name} — Tattoo Artist`} description={`${artist.name}, ${artist.role} at Obsidian Art & Tattoo Studio. ${artist.tagline}`} />
      <JsonLd id="artist-schema" data={{
        "@context": "https://schema.org",
        "@type": "Person",
        name: artist.name,
        jobTitle: artist.role,
        description: artist.bio,
        image: fileUrl(artist.photo),
        url: `https://www.obsidianinkstudio.com/artists/${artist.slug}`,
        knowsAbout: artist.styles,
        worksFor: { "@type": "TattooParlor", name: "Obsidian Art & Tattoo Studio", address: { "@type": "PostalAddress", streetAddress: "6801 Backlick Rd Suite C", addressLocality: "Springfield", addressRegion: "VA", postalCode: "22150", addressCountry: "US" } },
        sameAs: artist.instagram ? [`https://instagram.com/${artist.instagram}`] : [],
      }} />

      {/* HERO split */}
      <section data-testid="artist-hero" className="pt-20 border-b border-ash">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="mono-hover relative aspect-[3/4] lg:aspect-auto lg:min-h-[86vh] overflow-hidden border-b lg:border-b-0 lg:border-r border-ash">
            <img src={fileUrl(artist.photo)} alt={artist.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 to-transparent lg:hidden" />
          </div>
          <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-0 flex flex-col justify-center">
            <Reveal>
              <Link to="/artists" className="inline-flex items-center gap-2 text-[#828290] font-mono text-[0.7rem] uppercase tracking-[0.2em] mb-8 hover:text-white transition-colors">
                <ArrowLeft size={14} /> All artists
              </Link>
            </Reveal>
            <Reveal delay={60}><p className="eyebrow mb-4">{artist.role} — {artist.experience}</p></Reveal>
            <Reveal delay={100}><h1 className="display text-7xl lg:text-8xl text-white">{artist.name}</h1></Reveal>
            <Reveal delay={160}><p className="mt-6 text-lg text-[#C4C4CC] max-w-lg">{artist.tagline}</p></Reveal>

            <Reveal delay={220}>
              <div className="mt-8 flex flex-wrap gap-2">
                {(artist.styles || []).map((s) => (
                  <Link key={s} to={`/styles/${s}`} className="text-[0.65rem] font-mono uppercase tracking-widest border border-ash text-[#C4C4CC] px-3 py-1.5 hover:border-ember hover:text-white transition-colors">
                    {styleMap[s] || s}
                  </Link>
                ))}
              </div>
            </Reveal>

            <Reveal delay={280}>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link to={`/booking?artist=${artist.slug}`} data-testid="artist-book-cta" className="inline-flex items-center gap-3 bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-white transition-colors">
                  Book with {artist.name} <ArrowRight size={14} />
                </Link>
                {artist.instagram && (
                  <a href={`https://instagram.com/${artist.instagram}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white font-mono text-xs uppercase tracking-[0.2em] link-underline">
                    <Instagram size={16} /> @{artist.instagram}
                  </a>
                )}
                {artist.email && (
                  <a href={`mailto:${artist.email}`} className="inline-flex items-center gap-2 text-[#828290] hover:text-white transition-colors"><Mail size={16} /></a>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BIO */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 py-24 border-b border-ash">
        <Reveal><p className="eyebrow mb-8">Biography</p></Reveal>
        <Reveal delay={60}>
          <p className="font-sans font-light text-2xl lg:text-3xl text-[#E8E8EC] leading-snug whitespace-pre-line">{artist.bio}</p>
        </Reveal>
      </section>

      {/* PORTFOLIO */}
      {portfolio.length > 0 && (
        <section data-testid="artist-portfolio" className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-4">Portfolio</p>
              <h2 className="display text-5xl lg:text-6xl text-white">Selected work.</h2>
            </div>
          </div>
          <div className="columns-2 lg:columns-3 gap-4 lg:gap-6 [column-fill:_balance]">
            {portfolio.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 60} className="mb-4 lg:mb-6 break-inside-avoid">
                <button onClick={() => setLightbox(fileUrl(p.image))} className="mono-hover img-zoom block w-full overflow-hidden border border-ash" data-testid={`artist-work-${p.id}`}>
                  <img src={fileUrl(p.image)} alt={`${artist.name} — ${styleMap[p.style_slug] || "tattoo"} on ${p.placement}`} loading="lazy" className="w-full object-cover" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-ash">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-28 text-center">
          <h2 className="display text-5xl lg:text-6xl text-white mb-8">Work with {artist.name}.</h2>
          <Link to={`/booking?artist=${artist.slug}`} className="inline-flex items-center gap-3 bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-10 py-5 hover:bg-white transition-colors">
            Book a Session <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
